import axios from "axios";
import { axiosInstance } from "./index";
import type { CampaignEngagementTerms, SaveCampaignEngagementTerms } from "./engagementTerms";

// Campaign User interface
export interface CampaignUser {
	uuid: string;
	name: string;
}

// Campaign Status Types
export enum CampaignStatusType {
	TODO = "To-do",
	IN_PROGRESS = "In Progress",
	COMPLETE = "Completed",
}

// Campaign Status Phases
export enum CampaignStatusPhase {
	PROPOSAL = "Proposal",
	BRIEFING = "Briefing",
	SHORTLISTING = "Shortlisting",
	SCRIPTING = "Scripting",
	VIDEO_PRODUCTION = "Video Production",
	GO_LIVE = "Go Live",
	COMPLETE = "Completed",
	CANCELLED = "Cancelled",
	PARKED = "Parked",
}

// Campaign Types
export enum CampaignType {
	TTO = "TTO",
	TIKTOK = "TikTok",
	META = "Meta",
	DIRECT = "Direct",
	WORKSHOP = "Workshop",
	LIVESTREAM = "Livestream",
	REMIX = "Remix",
	PRODUCTION = "Production",
	ALWAYS_ON = "Always On",
	PODCAST = "Podcast",
}

// Define the campaign data interface matching the backend model
/** Campaigns created before CET run without Engagement Terms. A missing flag counts as on. */
export const isCetEnabled = (campaign: { cetEnabled?: boolean } | null | undefined): boolean =>
	campaign?.cetEnabled !== false;

export interface Campaign {
	// Campaign Core Attributes
	campaignId: string;
	name: string;
	projectCode: string;
	status: CampaignStatusType;
	statusPhase: CampaignStatusPhase;
	type: CampaignType;
	totalBudget?: number | null;
	currency?: string | null;
	/** Campaign Engagement Terms flow on/off. Campaigns created before CET are off; read it with isCetEnabled. */
	cetEnabled?: boolean;
	/** Creator list only: false when the viewing creator was removed from the campaign. */
	isAvailable?: boolean;

	// Team and Roles
	amplifyTeam: string[];
	lead: CampaignUser[];
	support?: CampaignUser[];
	accountManager?: CampaignUser[];

	// Location and Client Info
	location: string[];
	client?: string[];
	brand?: string[];

	// Metadata
	lastModificationTime: string;
	entityType: string;
	createdAt: string;
	/** Current user's single non-audit role on this campaign. Present on list results. */
	viewerRole?: "Lead" | "Support" | "Account Manager" | "Client" | "Creator" | "Brand" | null;

	// Notion Integration
	entityId?: string;

	// Markets (from blocks)
	markets?: Array<{ name: string; status_phase: string }>;

	// Budget (from campaign_rate)
	finalRate?: number | null;
	finalCurrency?: string | null;
}

// Extended interface for table display (adds key for Ant Design Table)
export interface CampaignData extends Campaign {
	key: string; // Required by Ant Design Table
}

export class CampaignApiError extends Error {
	constructor(
		message: string,
		readonly status?: number,
		readonly code?: string,
		readonly campaignName?: string | null,
	) {
		super(message);
		this.name = "CampaignApiError";
	}
}

// Model validation
export function validateCampaign(campaign: Campaign): string {
	if (!campaign.name || campaign.name.length === 0) {
		return "Campaign name is required";
	}
	return "";
}

export interface AnalysisVideo {
	id: string;
	url: string;
	campaignId?: string;
	resourceId?: string;
	platform: "tiktok" | "instagram" | "youtube" | "facebook";
	thumbnails?: Record<string, string>;
	duration: string;
	viewCount: string;
	engagementRate: string;
	description: string;
	plays: string;
	likes: string;
	comments: string;
	shares: string;
	saves: string;
	postedDate: string;
	hookStrength?: string;
	benefitRatio?: string;
	sentimentScore?: string;
	aiAnalysis?: string;
	transcript: string;
	followerCount?: string;
	finalRate?: number;
	finalCurrency?: string;
	averageWatchTime?: string;
	retentionRate?: string;
	createdAt?: string;
	updatedAt?: string;
	lastAnalyzedAt?: string;
	author?: AuthorInfo; // Add creator information
}

export interface ReportAnalysisResponse {
	videos: AnalysisVideo[];
	pending: string[];
}

export interface AuthorInfo {
	handleName: string;
	avatarUrl: string;
	followerCount?: number;
}

// Helper function to map status phase to status type for coloring
export function getStatusTypeFromPhase(phase: CampaignStatusPhase): CampaignStatusType {
	switch (phase) {
		case CampaignStatusPhase.BRIEFING:
		case CampaignStatusPhase.PROPOSAL:
		case CampaignStatusPhase.PARKED:
			return CampaignStatusType.TODO;

		case CampaignStatusPhase.SHORTLISTING:
		case CampaignStatusPhase.SCRIPTING:
		case CampaignStatusPhase.VIDEO_PRODUCTION:
		case CampaignStatusPhase.GO_LIVE:
			return CampaignStatusType.IN_PROGRESS;

		case CampaignStatusPhase.COMPLETE:
		case CampaignStatusPhase.CANCELLED:
			return CampaignStatusType.COMPLETE;

		default:
			return CampaignStatusType.TODO;
	}
}

// Campaign API service
export const campaignAPI = {
	inviteCreator: async (
		campaignId: string,
		email: string,
		creatorAssignmentId?: string,
		message?: string,
		termsInput?: { marketBlockId: string; engagementTerms: SaveCampaignEngagementTerms },
		sendReminder = false,
	): Promise<{ expiresAt: string; terms?: CampaignEngagementTerms }> => {
		const response = await axiosInstance.post(`/campaigns/${campaignId}/creator-invitations`, {
			email,
			creatorAssignmentId,
			message,
			...termsInput,
			sendReminder,
		});
		return response.data;
	},

	// Get all campaigns
	getAll: async (): Promise<CampaignData[]> => {
		try {
			const response = await axiosInstance.get<Campaign[]>("/campaigns");
			// Transform backend data to include key for Ant Design Table
			const campaignData: CampaignData[] = response.data.map((campaign: Campaign) => ({
				...campaign,
				key: campaign.campaignId, // Use campaignId as key
			}));
			return campaignData;
		} catch (error) {
			console.error("Error fetching campaigns:", error);
			throw new Error("Failed to fetch campaigns");
		}
	},

	// Get campaign by ID
	getById: async (id: string): Promise<CampaignData> => {
		try {
			// No request-side Cache-Control: it isn't CORS-safelisted and isn't in
			// either backend allow-list, which blocks this request entirely on any
			// cross-origin deploy. The backend already sends a no-store response
			// header (campaign_controller.ts), which is what actually prevents caching.
			const response = await axiosInstance.get<Campaign>(`/campaigns/${id}`);
			const campaignData: CampaignData = {
				...response.data,
				key: response.data.campaignId,
			};
			return campaignData;
		} catch (error) {
			console.error(`Error fetching campaign with ID ${id}:`, error);
			if (axios.isAxiosError(error)) {
				const data = error.response?.data as
					| { message?: string; error?: string; code?: string; campaignName?: string | null }
					| undefined;
				throw new CampaignApiError(
					data?.message || data?.error || `Failed to fetch campaign with ID ${id}`,
					error.response?.status,
					data?.code,
					data?.campaignName,
				);
			}
			throw new CampaignApiError(`Failed to fetch campaign with ID ${id}`);
		}
	},

	// Create new campaign
	create: async (campaignData: Omit<Campaign, "campaignId" | "createdAt" | "entityType">): Promise<CampaignData> => {
		try {
			const response = await axiosInstance.post<Campaign | { message: string; campaign: Campaign }>(
				"/campaigns",
				campaignData,
			);
			const createdCampaign = "campaign" in response.data ? response.data.campaign : response.data;
			const newCampaignData: CampaignData = {
				...createdCampaign,
				key: createdCampaign.campaignId,
			};
			return newCampaignData;
		} catch (error) {
			console.error("Error creating campaign:", error);
			throw new Error("Failed to create campaign");
		}
	},

	// Update campaign (full body; backend returns { message, campaign })
	update: async (id: string, campaignData: Partial<Campaign> & { campaignId?: string }): Promise<CampaignData> => {
		try {
			const body = { ...campaignData, campaignId: campaignData.campaignId ?? id };
			const response = await axiosInstance.put<{ message: string; campaign: Campaign }>(`/campaigns/${id}`, body);
			const c = response.data.campaign;
			return { ...c, key: c.campaignId };
		} catch (error) {
			console.error(`Error updating campaign with ID ${id}:`, error);
			throw new Error(`Failed to update campaign with ID ${id}`);
		}
	},

	/** Partial update for manage-campaign tab. Auth enforced by backend canManageCampaign. */
	patchMetadata: async (
		id: string,
		payload: {
			name?: string;
			projectCode?: string;
			client?: string[];
			type?: CampaignType;
			status?: CampaignStatusType;
			statusPhase?: CampaignStatusPhase;
			totalBudget?: number | null;
			currency?: string | null;
			/** Admin-only; the server keeps the stored value for everyone else. */
			cetEnabled?: boolean;
		},
	): Promise<CampaignData> => {
		try {
			const response = await axiosInstance.patch<{ message: string; campaign: Campaign }>(
				`/campaigns/${id}/metadata`,
				payload,
			);
			const c = response.data.campaign;
			return { ...c, key: c.campaignId };
		} catch (error: unknown) {
			console.error(`Error patching campaign metadata ${id}:`, error);
			const msg =
				(error as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message ||
				(error as { response?: { data?: { error?: string } } })?.response?.data?.error;
			throw new Error(msg || `Failed to update campaign ${id}`);
		}
	},

	// Remove a campaign from normal views. The backend retains it for the
	// configured retention period rather than physically deleting it.
	delete: async (id: string): Promise<void> => {
		try {
			await axiosInstance.delete(`/campaigns/${id}`);
		} catch (error: unknown) {
			console.error(`Error deleting campaign with ID ${id}:`, error);
			const responseData = (error as { response?: { data?: { message?: string; error?: string } } })?.response
				?.data;
			throw new Error(
				responseData?.message || responseData?.error || "We couldn't remove this campaign. Please try again.",
			);
		}
	},

	// Get campaign participants
	getParticipants: async (
		campaignId: string,
	): Promise<
		Array<{
			userId: string;
			name: string;
			email: string;
			role: string;
		}>
	> => {
		try {
			const response = await axiosInstance.get(`/campaigns/${campaignId}/participants`);

			// Filter out system roles (Created By, Last Modified By)
			const SYSTEM_ROLES = ["Created By", "Last Modified By"];
			const filteredParticipants = response.data.participants.filter(
				(participant: { role: string }) => !SYSTEM_ROLES.includes(participant.role),
			);

			return filteredParticipants;
		} catch (error) {
			console.error(`Error fetching participants for campaign ${campaignId}:`, error);
			throw new Error("Failed to fetch campaign participants");
		}
	},

	addParticipant: async (campaignId: string, email: string, role: string): Promise<{ message: string }> => {
		try {
			const response = await axiosInstance.post(`/campaign/${campaignId}/participant`, {
				email,
				role,
			});
			return response.data;
		} catch (error: unknown) {
			console.error(`Error adding participant to campaign ${campaignId}:`, error);

			const backendMessage = axios.isAxiosError(error)
				? (error.response?.data?.error as string | undefined)
				: undefined;
			if (axios.isAxiosError(error) && error.response?.status === 403) {
				throw new Error(backendMessage || "You are not authorized to add participants");
			}

			throw new Error(backendMessage || "Failed to add participant");
		}
	},

	removeParticipant: async (campaignId: string, userId: string): Promise<{ message: string }> => {
		try {
			const response = await axiosInstance.delete(`/campaign/${campaignId}/participant/${userId}`);
			return response.data;
		} catch (error) {
			console.error(`Error removing participant from campaign ${campaignId}:`, error);
			throw new Error("Failed to remove participant");
		}
	},

	// Manage Campaign "Save Changes" — applies a queue of buffered edits
	// atomically server-side. The backend runs ALL ops in one tx so either
	// everything commits or nothing does. On failure the response body
	// carries `failedOp` + `failedOpIndex` so the UI can name the failing
	// change in its toast. See backend/src/service/batch_save_service.ts.
	saveChanges: async (
		campaignId: string,
		ops: unknown[],
	): Promise<{ tempIdMap: Record<string, string>; opCount: number }> => {
		try {
			const response = await axiosInstance.post(`/campaign/${campaignId}/save-changes`, { ops });
			return response.data;
		} catch (error: any) {
			// Surface backend's structured error so callers can show a
			// precise per-op message instead of a generic "save failed".
			const data = error?.response?.data;
			const message = data?.message || error?.message || "Failed to save changes";
			const wrapped = new Error(message) as Error & {
				failedOp?: unknown;
				failedOpIndex?: number;
				statusCode?: number;
			};
			wrapped.failedOp = data?.failedOp;
			wrapped.failedOpIndex = data?.failedOpIndex;
			wrapped.statusCode = error?.response?.status;
			throw wrapped;
		}
	},

	// Check if user is assigned to campaign
	isUserAssignedToCampaign: async (
		campaignId: string,
	): Promise<{
		campaignId: string;
		userId: string;
		isAssigned: boolean;
		roles: string[];
	}> => {
		try {
			const response = await axiosInstance.get(`/campaigns/${campaignId}/is-user-assigned`);
			return response.data;
		} catch (error) {
			console.error(`Error checking user assignment for campaign ${campaignId}:`, error);
			throw new Error("Failed to check user assignment");
		}
	},

	// Get cached campaign analysis; missing cards are queued and returned as pending.
	getReportAnalysis: async (campaignId: string, signal?: AbortSignal): Promise<ReportAnalysisResponse> => {
		try {
			const response = await axiosInstance.post<ReportAnalysisResponse | AnalysisVideo[]>(
				`/campaigns/${campaignId}/report/analysis`,
				undefined,
				{ signal },
			);

			const data = response.data;
			if (Array.isArray(data)) {
				return { videos: data, pending: [] };
			}
			if (data && Array.isArray(data.videos)) {
				return {
					videos: data.videos,
					pending: Array.isArray(data.pending) ? data.pending : [],
				};
			}

			console.error("Unexpected response format from getReportAnalysis:", data);
			return { videos: [], pending: [] };
		} catch (error: unknown) {
			if (axios.isCancel(error)) throw error;
			console.error(`Error fetching campaign analysis for ${campaignId}:`, error);
			throw new Error("Failed to fetch campaign analysis");
		}
	},

	// Generate AI analysis for campaign
	generateAIAnalysis: async (campaignId: string): Promise<AnalysisVideo[]> => {
		try {
			const response = await axiosInstance.get<{ videos: AnalysisVideo[] }>(
				`/campaigns/${campaignId}/report/ai-analysis`,
			);
			return response.data.videos || [];
		} catch (error) {
			console.error(`Error generating AI analysis for campaign ${campaignId}:`, error);
			throw new Error("Failed to generate AI analysis");
		}
	},

	// Get time-series metrics for campaign report chart
	getReportTimeseries: async (
		campaignId: string,
		params: { platform?: string; market?: string; period?: string },
	): Promise<TimeseriesResponse> => {
		try {
			const response = await axiosInstance.get<TimeseriesResponse>(`/campaigns/${campaignId}/report/timeseries`, {
				params,
			});
			return response.data;
		} catch (error) {
			console.error(`Error fetching timeseries for campaign ${campaignId}:`, error);
			throw new Error("Failed to fetch timeseries data");
		}
	},
};

// --- Timeseries types ---

export interface TimeseriesDayPoint {
	date: string;
	views: number;
	engagements: number;
	likes: number;
	comments: number;
	shares: number;
	saves: number;
}

export interface TimeseriesTotals {
	views: number;
	avgEngagementRate: number;
	likes: number;
	comments: number;
	shares: number;
	saves: number;
	engagements: number;
}

export interface TimeseriesResponse {
	campaignId: string;
	platform: string;
	market: string;
	period: string;
	dataAccurateUntil: string | null;
	oldestDataAccurateUntil: string | null;
	latestDataAt: string | null;
	trackedVideoCount: number;
	readyVideoCount: number;
	coveragePercentage: number;
	historyArchivedBefore: string | null;
	videoCount: number;
	metricAvailability: {
		shares: boolean;
		saves: boolean;
	};
	observationDays: string[];
	series: TimeseriesDayPoint[];
	totals: TimeseriesTotals;
}

// Export default
export default campaignAPI;
